<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">
    <!-- Page title -->
    <div class="mb-6">
      <h1 class="text-3xl font-bold text-gray-900 leading-7">Customers</h1>
    </div>

    <!-- Top controls (Search + New user) -->
    <div class="flex items-center justify-between gap-4 mb-4">
      <div class="w-64">
        <div class="relative">
          <input
            v-model="topSearch"
            type="text"
            placeholder="Search"
            class="w-full h-9 rounded-lg border border-gray-300 px-9 text-sm focus:outline-none focus:ring-0"
            :disabled="loading"
          />
        </div>
      </div>

      <button
        v-if="activeTab === 'new'"
        type="button"
        class="inline-flex items-center gap-2 h-9 px-4 rounded-full font-bold text-white text-sm shadow-sm hover:opacity-95"
        style="background-color: #5b9ac5"
        @click="openAdd"
        :disabled="loading"
      >
        <span class="text-base leading-none">＋</span>
        New user
      </button>
    </div>

    <!-- Big Box -->
    <DynamicTable
      wrapperClass="max-w-full overflow-x-auto"
      :rows="currentRows"
      :columns="currentColumns"
      :showHeader="false"
      :externalSearch="topSearch"
      :rowsPerPage="50"
      :rowsPerPageOptions="[10, 20, 50]"
    >
      <!-- Tabs INSIDE the big card -->
      <template #tabs>
        <div class="pt-5">
          <div class="flex justify-center gap-14">
            <button
              type="button"
              class="pb-3 text-lg font-semibold"
              :class="activeTab === 'new' ? 'text-gray-900' : 'text-gray-400'"
              @click="setTab('new')"
            >
              New Customers
            </button>
            <button
              type="button"
              class="pb-3 text-lg font-semibold"
              :class="activeTab === 'our' ? 'text-gray-900' : 'text-gray-400'"
              @click="setTab('our')"
            >
              Our Customers
            </button>
            <button
              type="button"
              class="pb-3 text-lg font-semibold"
              :class="activeTab === 'company' ? 'text-gray-900' : 'text-gray-400'"
              @click="setTab('company')"
            >
              Company
            </button>
            <button
              type="button"
              class="pb-3 text-lg font-semibold"
              :class="activeTab === 'black' ? 'text-gray-900' : 'text-gray-400'"
              @click="setTab('black')"
            >
              Black list
            </button>
          </div>

          <div class="mt-4 border-b border-gray-100"></div>
        </div>
      </template>

      <!-- Action slot -->
      <template #action="{ row }">
        <div class="flex items-center justify-center gap-4 whitespace-nowrap">
          <template v-if="activeTab === 'black'">
            <span class="text-gray-500">----</span>
          </template>

          <template v-else>
            <!-- Edit -->
            <div class="flex items-center justify-center">
              <button
                class="inline-flex items-center gap-1.5 text-xs text-black hover:opacity-90"
                title="Edit"
                @click="openEdit(row)"
                :disabled="loading"
              >
                <svg viewBox="0 0 24 24" class="w-4 h-4" aria-hidden="true">
                  <path
                    d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm18-11.5a1 1 0 0 0 0-1.41L18.66 3a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75L21 5.75Z"
                    fill="currentColor"
                  />
                </svg>
                <span>Edit</span>
              </button>
            </div>

            <!-- Delete -->
            <button
              class="inline-flex items-center gap-1 text-black hover:text-red-700"
              @click="deleteRow(row)"
              :disabled="loading"
            >
              <i class="fas fa-trash-alt text-red-600"></i>
              <span>Delete</span>
            </button>
          </template>
        </div>
      </template>
    </DynamicTable>

    <!-- Modal (Add/Edit) -->
    <div
      v-if="isModalOpen"
      class="fixed inset-0 flex items-start justify-center z-[9999] px-4 pt-32"
      style="
        background: rgba(15, 23, 42, 0.55);
        backdrop-filter: blur(6px);
        -webkit-backdrop-filter: blur(6px);
      "
    >
      <div
        class="customer-modal bg-white rounded-[18px] shadow-lg relative"
        style="
          width: 660px !important;
          max-width: 92vw !important;
          max-height: calc(100vh - 140px) !important;
          overflow: auto !important;
        "
      >
        <!-- Close (ONLY way to close manually) -->
        <button
          type="button"
          class="absolute right-5 top-4 text-gray-600 hover:text-gray-900 text-lg"
          aria-label="Close"
          @click="closeModal"
        >
          ✕
        </button>

        <div class="px-3 pt-3 pb-3">
          <h3 class="text-center text-[18px] mt-4 font-bold text-gray-900">
            {{ modalMode === "add" ? "Add User" : "Edit User" }}
          </h3>

          <form class="mt-6 p-2" @submit.prevent="submitModal">
            <!-- First + Last -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
              <div>
                <label class="block text-xs font-semibold text-gray-700 mb-2"
                  >First Name</label
                >
                <input
                  v-model.trim="modalModel.first_name"
                  type="text"
                  placeholder="Enter first name"
                  class="w-full h-10 rounded-xl border border-gray-300 px-3 text-sm focus:outline-none"
                />
                <p
                  v-if="fieldError('first_name')"
                  class="text-xs text-red-600 mt-1"
                >
                  {{ fieldError("first_name") }}
                </p>
              </div>

              <div>
                <label class="block text-xs font-semibold text-gray-700 mb-2"
                  >Last Name</label
                >
                <input
                  v-model.trim="modalModel.last_name"
                  type="text"
                  placeholder="Enter last name"
                  class="w-full h-10 rounded-xl border border-gray-300 px-3 text-sm focus:outline-none"
                />
                <p
                  v-if="fieldError('last_name')"
                  class="text-xs text-red-600 mt-1"
                >
                  {{ fieldError("last_name") }}
                </p>
              </div>
            </div>

            <!-- Country Code + Phone -->
            <div class="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-5">
              <!-- Country Code dropdown (restcountries) -->
              <div class="col-span-2 relative">
                <label class="block text-xs font-semibold text-gray-700 mb-2"
                  >Country Code</label
                >

                <input
                  v-model="countrySearch"
                  type="text"
                  placeholder="Search"
                  class="w-full h-10 rounded-xl border border-gray-300 px-3 text-sm focus:outline-none"
                  @focus="countryDropdownOpen = true"
                  @blur="hideCountryDropdownWithDelay"
                  :disabled="submitting"
                />

                <div
                  v-show="countryDropdownOpen"
                  class="absolute left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden w-full"
                >
                  <div
                    v-if="countriesLoading"
                    class="px-3 py-3 text-sm text-gray-500"
                  >
                    Loading...
                  </div>

                  <div v-else class="max-h-56 overflow-y-auto">
                    <button
                      v-for="item in filteredCountryCodes"
                      :key="item.key"
                      type="button"
                      class="w-full px-2 py-1.5 text-[12px] hover:bg-gray-50 flex items-center gap-2"
                      @mousedown.prevent="selectCountryCode(item)"
                    >
                      <img
                        :src="item.flag"
                        class="w-4 h-4 rounded-full shrink-0"
                        alt=""
                      />

                      <span class="min-w-0 flex-1 truncate text-left">
                        {{ item.name }}
                      </span>

                      <span class="shrink-0 text-gray-700 font-semibold">
                        ({{ item.code }})
                      </span>
                    </button>

                    <div
                      v-if="!filteredCountryCodes.length"
                      class="px-3 py-3 text-sm text-gray-500"
                    >
                      No results
                    </div>
                  </div>
                </div>

                <p
                  v-if="fieldError('country_code')"
                  class="text-xs text-red-600 mt-1"
                >
                  {{ fieldError("country_code") }}
                </p>
              </div>

              <!-- Phone -->
              <div class="col-span-2">
                <label class="block text-xs font-semibold text-gray-700 mb-2"
                  >Phone Number</label
                >
                <input
                  v-model="modalModel.phone_number"
                  type="text"
                  placeholder="Enter phone number"
                  class="w-full h-10 rounded-xl border border-gray-300 px-3 text-sm focus:outline-none"
                />
                <p v-if="fieldError('phone')" class="text-xs text-red-600 mt-1">
                  {{ fieldError("phone") }}
                </p>
              </div>
            </div>

            <!-- Note -->
            <div class="mb-6">
              <label class="block text-xs font-semibold text-gray-700 mb-2"
                >Note</label
              >
              <textarea
                v-model="modalModel.note"
                rows="3"
                placeholder="Enter Note"
                class="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none resize-none"
              ></textarea>
              <p v-if="fieldError('note')" class="text-xs text-red-600 mt-1">
                {{ fieldError("note") }}
              </p>
            </div>

            <!-- Blacklist + Company -->
            <div class="flex items-center justify-between mb-6">
              <label
                class="inline-flex items-center gap-2 text-sm font-semibold text-gray-700"
              >
                <input
                  v-model="modalModel.blacklist"
                  type="checkbox"
                  class="w-4 h-4 rounded border-gray-300"
                />
                <span>Blacklist</span>
              </label>

              <label
                class="inline-flex items-center gap-2 text-sm font-semibold text-gray-700"
              >
                <input
                  v-model="modalModel.company"
                  type="checkbox"
                  class="w-4 h-4 rounded border-gray-300"
                />
                <span>Company</span>
              </label>
            </div>

            <p v-if="generalError" class="text-sm text-red-600 mb-3">
              {{ generalError }}
            </p>

            <button
              type="submit"
              class="w-full h-11 rounded-full font-bold text-white text-sm hover:opacity-95 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style="background-color: #5b9ac5"
              :disabled="submitting"
            >
              <template v-if="submitting">
                <svg
                  class="animate-spin h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>

                <span>
                  {{ modalMode === "add" ? "Adding..." : "Saving..." }}
                </span>
              </template>

              <template v-else>
                {{ modalMode === "add" ? "Add" : "Save" }}
              </template>
            </button>
          </form>
        </div>
      </div>
    </div>

    <!-- loading overlay -->
    <div
      v-if="loading"
      class="fixed top-0 left-0 w-screen h-screen flex flex-col justify-center items-center z-50 pointer-events-none"
    >
      <div class="loader"></div>
    </div>
  </div>
</template>

<script>
import DynamicTable from "@/components/DynamicTable.vue";
import Swal from "sweetalert2";
import { mapActions, mapState } from "vuex";

export default {
  name: "CustomerList",
  components: { DynamicTable },

  data() {
    return {
      activeTab: "new",
      topSearch: "",

      isModalOpen: false,
      modalMode: "add",
      editingId: null,
      submitting: false,

      modalModel: {
        first_name: "",
        last_name: "",
        country_code: "",
        phone_number: "",
        note: "",
        blacklist: false,
        company: false,
      },

      formErrors: {},
      generalError: "",

      loading: false,
      debounceTimer: null,

      countryApiData: [],
      countriesLoading: false,
      countryDropdownOpen: false,
      countrySearch: "",
    };
  },

  computed: {
    ...mapState(["customers"]),

    currentRows() {
      const c = this.customers || {};
      let rows = [];

      if (this.activeTab === "our") rows = c.our_customers || [];
      else if (this.activeTab === "black") rows = c.blacklist || [];
      else if (this.activeTab === "company") rows = c.company_customers || [];
      else rows = c.new_customers || [];

      return rows.map((r) => {
        const fullName = `${r?.first_name || ""} ${r?.last_name || ""}`.trim();
        const phoneNumber = `${r?.country_code || ""}${r?.phone || ""}`.trim();
        
        let reasonText = "";
        if (r?.reason) {
          try {
            const reasonObj = typeof r.reason === "string" ? JSON.parse(r.reason) : r.reason;
            reasonText = reasonObj?.reason || "";
          } catch (e) {
            reasonText = "";
          }
        }

        return { ...r, name: fullName, phone_number: phoneNumber, reason: reasonText };
      });
    },

    currentColumns() {
      if (this.activeTab === "our" || this.activeTab === "company") {
        return [
          { key: "id", label: "ID", align: "left", width: "10%" },
          { key: "name", label: "Name", align: "left", width: "35%" },
          { key: "phone_number", label: "Phone Number", align: "left", width: "20%" },
          { key: "email", label: "Email", align: "left", width: "20%" },
          { key: "action", label: "Action", align: "center", width: "20%" },
        ];
      }

      if (this.activeTab === "black") {
        return [
          { key: "id", label: "ID", align: "left", width: "10%" },
          { key: "name", label: "Name", align: "left", width: "30%" },
          { key: "phone_number", label: "Phone Number", align: "left", width: "20%" },
          { key: "reason", label: "Reason", align: "left", width: "40%" },
        ];
      }

      return [
        { key: "id", label: "ID", align: "left", width: "10%" },
        { key: "name", label: "Name", align: "left", width: "35%" },
        { key: "phone_number", label: "Phone Number", align: "left", width: "25%" },
        { key: "action", label: "Action", align: "center", width: "25%" },
      ];
    },

    filteredCountryCodes() {
      const term = String(this.countrySearch || "").toLowerCase().trim();
      const list = this.countryApiData || [];
      if (!term) return list;

      return list.filter(
        (c) =>
          String(c.name || "").toLowerCase().includes(term) ||
          String(c.code || "").includes(term)
      );
    },
  },

  methods: {
    ...mapActions(["fetchCustomers", "addCustomer", "updateCustomer", "deleteCustomer"]),

    fieldError(key) {
      const v = this.formErrors?.[key];
      if (Array.isArray(v)) return v[0] || "";
      return typeof v === "string" ? v : "";
    },

    clearErrors() {
      this.formErrors = {};
      this.generalError = "";
    },

    normalizeCountryCode(code) {
      const digits = String(code || "").replace(/[^\d]/g, "");
      return digits ? `+${digits}` : "";
    },

    normalizePhone(phone) {
      return String(phone || "").trim().replace(/[^\d]/g, "");
    },

async fetchCountryApiData() {
  if ((this.countryApiData || []).length) return;

  // قائمة static — ما في أي طلب خارجي، فما في CORS ولا server يوقع
  const COUNTRIES = [
    { name: "Afghanistan", dial: "+93", iso2: "af" },
    { name: "Albania", dial: "+355", iso2: "al" },
    { name: "Algeria", dial: "+213", iso2: "dz" },
    { name: "Andorra", dial: "+376", iso2: "ad" },
    { name: "Angola", dial: "+244", iso2: "ao" },
    { name: "Argentina", dial: "+54", iso2: "ar" },
    { name: "Armenia", dial: "+374", iso2: "am" },
    { name: "Australia", dial: "+61", iso2: "au" },
    { name: "Austria", dial: "+43", iso2: "at" },
    { name: "Azerbaijan", dial: "+994", iso2: "az" },
    { name: "Bahrain", dial: "+973", iso2: "bh" },
    { name: "Bangladesh", dial: "+880", iso2: "bd" },
    { name: "Belarus", dial: "+375", iso2: "by" },
    { name: "Belgium", dial: "+32", iso2: "be" },
    { name: "Bolivia", dial: "+591", iso2: "bo" },
    { name: "Bosnia and Herzegovina", dial: "+387", iso2: "ba" },
    { name: "Brazil", dial: "+55", iso2: "br" },
    { name: "Bulgaria", dial: "+359", iso2: "bg" },
    { name: "Cambodia", dial: "+855", iso2: "kh" },
    { name: "Cameroon", dial: "+237", iso2: "cm" },
    { name: "Canada", dial: "+1", iso2: "ca" },
    { name: "Chad", dial: "+235", iso2: "td" },
    { name: "Chile", dial: "+56", iso2: "cl" },
    { name: "China", dial: "+86", iso2: "cn" },
    { name: "Colombia", dial: "+57", iso2: "co" },
    { name: "Costa Rica", dial: "+506", iso2: "cr" },
    { name: "Croatia", dial: "+385", iso2: "hr" },
    { name: "Cuba", dial: "+53", iso2: "cu" },
    { name: "Cyprus", dial: "+357", iso2: "cy" },
    { name: "Czech Republic", dial: "+420", iso2: "cz" },
    { name: "Denmark", dial: "+45", iso2: "dk" },
    { name: "Djibouti", dial: "+253", iso2: "dj" },
    { name: "Dominican Republic", dial: "+1", iso2: "do" },
    { name: "Ecuador", dial: "+593", iso2: "ec" },
    { name: "Egypt", dial: "+20", iso2: "eg" },
    { name: "El Salvador", dial: "+503", iso2: "sv" },
    { name: "Estonia", dial: "+372", iso2: "ee" },
    { name: "Ethiopia", dial: "+251", iso2: "et" },
    { name: "Finland", dial: "+358", iso2: "fi" },
    { name: "France", dial: "+33", iso2: "fr" },
    { name: "Georgia", dial: "+995", iso2: "ge" },
    { name: "Germany", dial: "+49", iso2: "de" },
    { name: "Ghana", dial: "+233", iso2: "gh" },
    { name: "Greece", dial: "+30", iso2: "gr" },
    { name: "Guatemala", dial: "+502", iso2: "gt" },
    { name: "Honduras", dial: "+504", iso2: "hn" },
    { name: "Hong Kong", dial: "+852", iso2: "hk" },
    { name: "Hungary", dial: "+36", iso2: "hu" },
    { name: "Iceland", dial: "+354", iso2: "is" },
    { name: "India", dial: "+91", iso2: "in" },
    { name: "Indonesia", dial: "+62", iso2: "id" },
    { name: "Iran", dial: "+98", iso2: "ir" },
    { name: "Iraq", dial: "+964", iso2: "iq" },
    { name: "Ireland", dial: "+353", iso2: "ie" },
    { name: "Italy", dial: "+39", iso2: "it" },
    { name: "Ivory Coast", dial: "+225", iso2: "ci" },
    { name: "Japan", dial: "+81", iso2: "jp" },
    { name: "Jordan", dial: "+962", iso2: "jo" },
    { name: "Kazakhstan", dial: "+7", iso2: "kz" },
    { name: "Kenya", dial: "+254", iso2: "ke" },
    { name: "Kuwait", dial: "+965", iso2: "kw" },
    { name: "Kyrgyzstan", dial: "+996", iso2: "kg" },
    { name: "Laos", dial: "+856", iso2: "la" },
    { name: "Latvia", dial: "+371", iso2: "lv" },
    { name: "Lebanon", dial: "+961", iso2: "lb" },
    { name: "Libya", dial: "+218", iso2: "ly" },
    { name: "Lithuania", dial: "+370", iso2: "lt" },
    { name: "Luxembourg", dial: "+352", iso2: "lu" },
    { name: "Malaysia", dial: "+60", iso2: "my" },
    { name: "Maldives", dial: "+960", iso2: "mv" },
    { name: "Malta", dial: "+356", iso2: "mt" },
    { name: "Mauritania", dial: "+222", iso2: "mr" },
    { name: "Mexico", dial: "+52", iso2: "mx" },
    { name: "Moldova", dial: "+373", iso2: "md" },
    { name: "Monaco", dial: "+377", iso2: "mc" },
    { name: "Mongolia", dial: "+976", iso2: "mn" },
    { name: "Montenegro", dial: "+382", iso2: "me" },
    { name: "Morocco", dial: "+212", iso2: "ma" },
    { name: "Nepal", dial: "+977", iso2: "np" },
    { name: "Netherlands", dial: "+31", iso2: "nl" },
    { name: "New Zealand", dial: "+64", iso2: "nz" },
    { name: "Nicaragua", dial: "+505", iso2: "ni" },
    { name: "Nigeria", dial: "+234", iso2: "ng" },
    { name: "North Macedonia", dial: "+389", iso2: "mk" },
    { name: "Norway", dial: "+47", iso2: "no" },
    { name: "Oman", dial: "+968", iso2: "om" },
    { name: "Pakistan", dial: "+92", iso2: "pk" },
    { name: "Palestine", dial: "+970", iso2: "ps" },
    { name: "Palestine", dial: "+972", iso2: "ps" },
    { name: "Panama", dial: "+507", iso2: "pa" },
    { name: "Paraguay", dial: "+595", iso2: "py" },
    { name: "Peru", dial: "+51", iso2: "pe" },
    { name: "Philippines", dial: "+63", iso2: "ph" },
    { name: "Poland", dial: "+48", iso2: "pl" },
    { name: "Portugal", dial: "+351", iso2: "pt" },
    { name: "Qatar", dial: "+974", iso2: "qa" },
    { name: "Romania", dial: "+40", iso2: "ro" },
    { name: "Russia", dial: "+7", iso2: "ru" },
    { name: "Saudi Arabia", dial: "+966", iso2: "sa" },
    { name: "Senegal", dial: "+221", iso2: "sn" },
    { name: "Serbia", dial: "+381", iso2: "rs" },
    { name: "Singapore", dial: "+65", iso2: "sg" },
    { name: "Slovakia", dial: "+421", iso2: "sk" },
    { name: "Slovenia", dial: "+386", iso2: "si" },
    { name: "Somalia", dial: "+252", iso2: "so" },
    { name: "South Africa", dial: "+27", iso2: "za" },
    { name: "South Korea", dial: "+82", iso2: "kr" },
    { name: "Spain", dial: "+34", iso2: "es" },
    { name: "Sri Lanka", dial: "+94", iso2: "lk" },
    { name: "Sudan", dial: "+249", iso2: "sd" },
    { name: "Sweden", dial: "+46", iso2: "se" },
    { name: "Switzerland", dial: "+41", iso2: "ch" },
    { name: "Syria", dial: "+963", iso2: "sy" },
    { name: "Taiwan", dial: "+886", iso2: "tw" },
    { name: "Tajikistan", dial: "+992", iso2: "tj" },
    { name: "Tanzania", dial: "+255", iso2: "tz" },
    { name: "Thailand", dial: "+66", iso2: "th" },
    { name: "Tunisia", dial: "+216", iso2: "tn" },
    { name: "Turkey", dial: "+90", iso2: "tr" },
    { name: "Turkmenistan", dial: "+993", iso2: "tm" },
    { name: "Uganda", dial: "+256", iso2: "ug" },
    { name: "Ukraine", dial: "+380", iso2: "ua" },
    { name: "United Arab Emirates", dial: "+971", iso2: "ae" },
    { name: "United Kingdom", dial: "+44", iso2: "gb" },
    { name: "United States", dial: "+1", iso2: "us" },
    { name: "Uruguay", dial: "+598", iso2: "uy" },
    { name: "Uzbekistan", dial: "+998", iso2: "uz" },
    { name: "Venezuela", dial: "+58", iso2: "ve" },
    { name: "Vietnam", dial: "+84", iso2: "vn" },
    { name: "Yemen", dial: "+967", iso2: "ye" },
    { name: "Zambia", dial: "+260", iso2: "zm" },
    { name: "Zimbabwe", dial: "+263", iso2: "zw" },
  ];

  this.countryApiData = COUNTRIES.map((c, idx) => ({
    key: `${c.iso2}-${idx}`,
    name: c.name,
    code: this.normalizeCountryCode(c.dial),
    flag: `https://flagcdn.com/w40/${c.iso2}.png`,
  }));
},

    selectCountryCode(item) {
      const code = this.normalizeCountryCode(item?.code || "");
      this.modalModel.country_code = code;
      this.countrySearch = code;
      this.countryDropdownOpen = false;
    },

    hideCountryDropdownWithDelay() {
      setTimeout(() => {
        this.countryDropdownOpen = false;
      }, 200);
    },

    // ✅ التعديل هون: شلنا الـ search من الـ payload
    async reloadLocal() {
      this.loading = true;
      try {
        await this.fetchCustomers({ source: "local" });
      } finally {
        this.loading = false;
      }
    },

    toastSuccess(title) {
      return Swal.fire({
        icon: "success",
        title: title || "Done",
        toast: true,
        position: "top",
        timer: 1400,
        showConfirmButton: false,
      });
    },

    toastError(message) {
      return Swal.fire({
        icon: "error",
        title: "Error",
        text: message || "Something went wrong.",
        toast: true,
        position: "top",
        timer: 2200,
        showConfirmButton: false,
      });
    },

    setTab(tab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      this.closeModal();
    },

    openAdd() {
      this.clearErrors();
      this.modalMode = "add";
      this.editingId = null;

      this.modalModel = {
        first_name: "",
        last_name: "",
        country_code: "",
        phone_number: "",
        note: "",
        blacklist: false,
        company: false,
      };

      this.countrySearch = "";
      this.isModalOpen = true;
      this.fetchCountryApiData();
    },

    openEdit(row) {
      this.clearErrors();
      this.modalMode = "edit";
      this.editingId = row?.id ?? null;

      this.modalModel = {
        first_name: String(row?.first_name || "").trim(),
        last_name: String(row?.last_name || "").trim(),
        country_code: this.normalizeCountryCode(row?.country_code || ""),
        phone_number: String(row?.phone || "").trim(),
        note: row?.note || row?.address || "",
        blacklist: Boolean(row?.blacklist ?? row?.is_blacklist ?? row?.black_list ?? false),
        company: Boolean(row?.company ?? row?.is_company ?? false),
      };

      this.countrySearch = this.modalModel.country_code || "";
      this.isModalOpen = true;
      this.fetchCountryApiData();
    },

    closeModal() {
      this.isModalOpen = false;
      this.countryDropdownOpen = false;
    },

    debounceSearch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.reloadLocal();
      }, 450);
    },

    buildCustomerPayload() {
      const ccRaw = this.modalModel?.country_code || this.countrySearch || "";

      return {
        first_name: String(this.modalModel?.first_name ?? "").trim(),
        last_name: String(this.modalModel?.last_name ?? "").trim(),
        country_code: this.normalizeCountryCode(ccRaw),
        phone: this.normalizePhone(this.modalModel?.phone_number || ""),
        note: String(this.modalModel?.note || ""),
        blacklist: !!this.modalModel?.blacklist,
        company: !!this.modalModel?.company,
      };
    },

    async submitModal() {
      if (this.submitting) return;
      this.submitting = true;
      this.clearErrors();

      try {
        const payload = this.buildCustomerPayload();
        console.log("CUSTOMER PAYLOAD =>", payload);

        if (this.modalMode === "add") {
          const res = await this.addCustomer(payload);

          if (!res?.success) {
            this.generalError = res?.message || "Failed to add customer.";
            return;
          }

          await this.toastSuccess(res?.message || "Added successfully");
          this.closeModal();
          // ✅ التعديل هون: مسحنا البحث بعد الإضافة
          this.topSearch = "";
          await this.reloadLocal();
          return;
        }

        if (!this.editingId) return;

        await this.updateCustomer({ id: this.editingId, customerData: payload });

        await this.toastSuccess("Saved successfully");
        this.closeModal();
        await this.reloadLocal();
      } catch (e) {
        const status = e?.response?.status;
        const data = e?.response?.data;

        if (status === 422 && data?.errors) {
          this.formErrors = data.errors || {};
          this.generalError = data?.message || "";
          return;
        }

        this.generalError = data?.message || e?.message || "Something went wrong.";
      } finally {
        this.submitting = false;
      }
    },

    async deleteRow(row) {
      const id = row?.id;
      if (!id) return;
      if (this.activeTab === "black") return;

      const confirm = await Swal.fire({
        icon: "warning",
        title: "Delete customer?",
        text: "Are you sure you want to delete this customer?",
        showCancelButton: true,
        confirmButtonText: "Yes, delete",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#d33",
        cancelButtonColor: "#5B9AC5",
      });

      if (!confirm.isConfirmed) return;

      this.loading = true;
      try {
        await this.deleteCustomer(id);
        await this.reloadLocal();
        await this.toastSuccess("Deleted successfully");
      } catch (e) {
        await this.toastError(
          e?.response?.data?.message || e?.message || "Failed to delete."
        );
      } finally {
        this.loading = false;
      }
    },
  },

  created() {
    this.reloadLocal();
  },
};
</script>

<style scoped>
.loader {
  width: 46px;
  height: 46px;
  border-radius: 999px;
  border: 4px solid rgba(91, 154, 197, 0.3);
  border-top-color: #5b9ac5;
  animation: spin 0.9s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.customer-modal {
  width: 360px !important;
  max-width: 92vw !important;
}

:deep(table) {
  min-width: 900px;
}

:deep(th),
:deep(td) {
  white-space: nowrap;
}

:deep(td:last-child),
:deep(th:last-child) {
  white-space: nowrap;
}
</style>
